import type { CognosReportIR, TranslationResult } from "./api";

/**
 * Tiny localStorage-backed workflow store. Wave 1 keeps the entire
 * parse → translate → validate flow on the client; persistence
 * (history, named workflows) is a Wave 2+ concern.
 *
 * <p>A lightweight pub/sub on top of a {@link CustomEvent} lets the
 * Stepper re-render the moment a step completes — without dragging in
 * a full state-management library.
 */
const KEY_IR = "bridge.workflow.ir";
const KEY_TRANSLATION = "bridge.workflow.translation";
const CHANGE_EVENT = "bridge.workflow.changed";

function notify() {
  // CustomEvent on window survives strict mode + cross-component reads.
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function setLastIr(ir: CognosReportIR): void {
  localStorage.setItem(KEY_IR, JSON.stringify(ir));
  // A new IR invalidates any prior translation — keep the steps honest.
  localStorage.removeItem(KEY_TRANSLATION);
  notify();
}

export function getLastIr(): CognosReportIR | null {
  const raw = localStorage.getItem(KEY_IR);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CognosReportIR;
  } catch {
    return null;
  }
}

export function setLastTranslation(t: TranslationResult): void {
  localStorage.setItem(KEY_TRANSLATION, JSON.stringify(t));
  notify();
}

export function getLastTranslation(): TranslationResult | null {
  const raw = localStorage.getItem(KEY_TRANSLATION);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TranslationResult;
  } catch {
    return null;
  }
}

export function clearWorkflow(): void {
  localStorage.removeItem(KEY_IR);
  localStorage.removeItem(KEY_TRANSLATION);
  notify();
}

export function hasIr(): boolean {
  return localStorage.getItem(KEY_IR) !== null;
}

export function hasTranslation(): boolean {
  return localStorage.getItem(KEY_TRANSLATION) !== null;
}

/**
 * Subscribe to workflow changes. Returns an unsubscribe function.
 * Used by the Stepper so step badges update without remounting.
 */
export function onWorkflowChange(handler: () => void): () => void {
  const listener = () => handler();
  window.addEventListener(CHANGE_EVENT, listener);
  // localStorage emits a {@code storage} event when *another tab* writes;
  // hook that too so two browser tabs stay in sync.
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}
